import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { deepClone } from '@/utils/objects';
import { searchObjectsOnFields } from '@/utils/search';
import { RISK_PROFILES_TABLE_KEYS } from '@/enums/RiskProfiles';
import Locale from '@/types/locale';

interface UseRiskProfileSearchProps<T> {
  data: T[];
  locale: Locale;
  searchFields?: string[];
}

interface UseRiskProfileSearchReturn<T> {
  query: string;
  searching: boolean;
  tableData: T[];
  setQuery: (query: string) => void;
  clearSearch: () => void;
  onSearch: (query: string) => void;
}

export function useRiskProfileSearch<T>({ data, locale, searchFields }: UseRiskProfileSearchProps<T>): UseRiskProfileSearchReturn<T> {
  const [query, setQuery] = useState<string>('');
  const [searching, setSearching] = useState<boolean>(false);
  const [tableData, setTableData] = useState<T[]>(deepClone(data));

  // Update table data when source data changes and we're not actively searching
  useEffect(() => {
    if (!searching && query === '' && !isEqual(data, tableData)) {
      setTableData(deepClone(data));
    }
  }, [data, searching, tableData, query]);

  const clearSearch = () => {
    setTableData(data);
    setSearching(false);
    setQuery('');
  };

  const onSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    setSearching(true);

    if (!searchQuery) {
      return clearSearch();
    }

    const fieldsToSearch = searchFields || [`${RISK_PROFILES_TABLE_KEYS.NAME}.${locale}`, `profile.${RISK_PROFILES_TABLE_KEYS.TITLE}.${locale}`];

    const searchResult = searchObjectsOnFields(searchQuery, data, fieldsToSearch);

    setTableData(searchResult);
    setQuery(searchQuery);
    setSearching(false);
  };

  return {
    query,
    searching,
    tableData,
    setQuery,
    clearSearch,
    onSearch,
  };
}
